import { SimConstants, SimEventKind } from "@highmarch/domain";
import { impactGate } from "../render/statusFx";
import { announceEvents } from "./announcer";
import { EventCursor } from "./eventCursor";
import { LandingWait, killLandings } from "./impactWaits";
import type { LevelParts } from "./levelParts";

/** The level's landings wait on the one ImpactGate its router lands (render/statusFx.ts). */
const LANDING_WAIT: LandingWait = (key, enemy, fn) => impactGate.wait(key, enemy, fn);

/**
 * The level's own readers of the sim's new events — the unit views' tick hooks, the kill rewards and moments that
 * wait on their rounds' landings, the effects, the announcer and the guide — after each tick or accepted command.
 */
export class LevelTicks {
  /** How far the level's own readers (rewards, moments, announcements, the guide) have read this tick's events. */
  private readonly cursor = new EventCursor();

  constructor(private readonly p: LevelParts) {}

  /**
   * The frame loop's ticks: steps the sim up to `due` ticks (none past its end), each read as it lands. `checkpoint`:
   * a wave was cleared on one of them.
   */
  run(due: number): { ticks: number; checkpoint: boolean } {
    const p = this.p;
    let ticks = 0, checkpoint = false;
    while (ticks < due && !p.sim.isOver) {
      p.session.tick();
      ticks++;
      if (p.sim.events.has(SimEventKind.StageCleared)) checkpoint = true;
      this.afterTick();
    }
    return { ticks, checkpoint };
  }

  /** After one sim tick of the frame loop. */
  private afterTick(): void {
    const p = this.p, from = this.cursor.next(p.sim);
    p.scene.onTick();
    this.observeLandings(from);                          // before the router settles the tick's unclaimed shots
    p.scene.effects.onTick();
    p.rare.observe(from);                                // M36: after the views met the rare (its shine, its bar)
    p.wyrm.observe(from);                                // M40: the Starfall Wyrm, likewise
    p.pet.observe(from);                                 // Pets v2: the wild pup's beats
    announceEvents(p.hud, p.sim, p.rig, p.scene.grid, from);
    p.guide.observe(false, from);
  }

  /** After one headless tick (debugAdvance): no announcements, and the effects step with the tick. */
  afterDebugTick(): void {
    const p = this.p, from = this.cursor.next(p.sim);
    p.scene.onTick();
    this.observeLandings(from);
    p.scene.effects.onTick(); p.scene.effects.update(1 / SimConstants.TicksPerSecond);
    p.rare.observe(from);
    p.wyrm.observe(from);
    p.pet.observe(from);
    p.guide.observe(false, from);
  }

  /**
   * After an accepted command. Its events (NEXT WAVE's StageStarted, a card's TowerUpgraded) sit in the buffer until
   * the next step clears it, so the per-tick hooks never see them: run the ones that react to events here (only the
   * new ones).
   */
  afterCommand(): void {
    const p = this.p, from = this.cursor.next(p.sim);
    this.observeLandings(from);
    p.scene.effects.onTick();
    p.rare.observe(from);
    p.wyrm.observe(from);                                // a paid retry's WyrmRetried / WyrmSpawned come from a command
    p.pet.observe(from);                                 // the tap's WildPetEngaged comes from a command
    announceEvents(p.hud, p.sim, p.rig, p.scene.grid, from);
    p.guide.observe(true, from);
  }

  /**
   * The tick's new kills: their rewards wait in the points pill, and the moments they make wait with them, for the
   * killing rounds to land (the enemy view holds the deaths on the same landings). Rounds fly once the router is up.
   */
  private observeLandings(from: number): void {
    const p = this.p, sim = p.sim;
    const landings = killLandings(sim.events.all(), from, sim.tick, p.scene.router !== null);
    p.rewards.observe(landings, LANDING_WAIT);
    p.moments.observe(sim, from, landings, LANDING_WAIT);
  }
}
